import { Component, EventEmitter, Inject, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, Validators, FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { ReasonEnum, TrueFalseEnum, VendorEnum } from 'src/app/shared/enums/decline-offer.enum';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { DeclineOfferUpdate, DeclineReasonsPanel, VendorOptions } from 'src/app/shared/models/offers.model';

interface OfferDeclineForm {
    reasonId: FormControl<number | null>;
    vendorId: FormControl<VendorOptions | null>;
    vendorName: FormControl<string>;
    subReasonIds: FormControl<ListItem[]>;
    pay: FormControl<number | null>;
    payRate: FormControl<number | null>;
    payFreq: FormControl<string>;
    isExistingVendorForCandidate: FormControl<boolean | null>;
    facilityId: FormControl<ListItem | null>;
    facilityName: FormControl<string>;
    reasonText: FormControl<string>;
    otherReasonText: FormControl<string>;
    selectedReason: FormControl<DeclineReasonsPanel | null>;
}

@Owner('CodeBusters')
@UntilDestroy()
@Component({
    selector: 'nova-decline-panel',
    templateUrl: './decline-panel.component.html',
    styleUrls: ['./decline-panel.component.scss']
})
export class DeclinePanelComponent implements OnInit {
    @Output() doVendorSearch = new EventEmitter();
    @Output() doFacilitySearch = new EventEmitter();
    @ViewChild('slideOutPanel') private readonly slideOutPanel!: ElementRef<HTMLDivElement>;
    offerDeclineFormGroup: FormGroup<OfferDeclineForm>;
    title: string;
    declineReasons: DeclineReasonsPanel[];
    subReasonsList: ListItem[];
    trueFalseEnum = TrueFalseEnum;
    reasonEnum = ReasonEnum;
    vendorEnum = VendorEnum;
    incomingDeclineData: any;
    showPayFields = false;
    disableSubReasonUnknown = false;
    disableSubReasonOtherOptions = false;
    showOtherTextField = false;
    isAgencyLoading = false;
    isFacilityLoading = false;
    featureFlag = FeatureFlag;
    uniqueCategories: Array<{ categoryId: number; categoryName: string }> = [];
    filteredReasons: DeclineReasonsPanel[] = [];
    selectedReasonId: ListItem[] | null = null;
    subReasonQuestionLabel: string;

    constructor(
        public dialogRef: MatDialogRef<DeclinePanelComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.incomingDeclineData = data;
        this.title = data.title as string;
        this.declineReasons = data.declineReasons as DeclineReasonsPanel[];
    }

    ngOnInit(): void {
        this.offerDeclineFormGroup = new FormGroup<OfferDeclineForm>({
            reasonId: new FormControl<number | null>(null, [Validators.required]),
            vendorId: new FormControl<VendorOptions | null>(null),
            vendorName: new FormControl<string>(''),
            subReasonIds: new FormControl<ListItem[]>([]),
            pay: new FormControl<number | null>(null),
            payRate: new FormControl<number | null>({ value: null, disabled: true }, [Validators.pattern('^[0-9]*$')]),
            payFreq: new FormControl<string>({ value: 'week', disabled: true }),
            isExistingVendorForCandidate: new FormControl<boolean | null>(null),
            facilityId: new FormControl<ListItem | null>(null),
            facilityName: new FormControl<string>(''),
            reasonText: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.maxLength(999)]
            }),
            otherReasonText: new FormControl<string>(''),
            selectedReason: new FormControl<DeclineReasonsPanel | null>(null)
        });

        this.offerDeclineFormGroup
            .get('pay')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((val) => {
                const isPayInputEnabled = Number(val) === Number(this.trueFalseEnum.True);

                const payRateControl = this.offerDeclineFormGroup.controls.payRate;
                const payFreqControl = this.offerDeclineFormGroup.controls.payFreq;

                if (isPayInputEnabled) {
                    payRateControl.setValidators([Validators.required, Validators.pattern('^[0-9]*$')]);
                    payFreqControl.setValidators(Validators.required);
                    payRateControl.enable();
                    payFreqControl.enable();
                } else {
                    payRateControl.clearValidators();
                    payFreqControl.clearValidators();
                    payRateControl.disable();
                    payFreqControl.disable();
                    payRateControl.setValue(null);
                }

                payRateControl.updateValueAndValidity();
                payFreqControl.updateValueAndValidity();
            });

        this.offerDeclineFormGroup
            .get('subReasonIds')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((val) => {
                const otherReasonTextControl = this.offerDeclineFormGroup.get('otherReasonText');
                const selectedIds = (val as Array<{ id: number }> | null)?.map((reason) => reason.id) || [];
                this.disableSubReasonUnknown = selectedIds.length === 0 ? false : true;
                this.updateSelectedReasons();
                if (selectedIds.includes(this.reasonEnum.Other)) {
                    this.showOtherTextField = true;
                    otherReasonTextControl.setValidators(Validators.required);
                } else {
                    this.showOtherTextField = false;
                    otherReasonTextControl.clearValidators();
                }
                otherReasonTextControl.updateValueAndValidity();

                this.showPayFields = selectedIds.includes(this.reasonEnum.BetterPay);
                if (this.showPayFields) {
                    this.offerDeclineFormGroup.get('payFreq')?.setValue('week');
                }
            });

        this.offerDeclineFormGroup
            .get('vendorId')
            .valueChanges.pipe(
                filter((data: string | VendorOptions) => typeof data === 'string' && data.trim().length > 2),
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.isAgencyLoading = true;
                }),
                untilDestroyed(this)
            )
            .subscribe((data) => {
                this.doVendorSearch.emit(data);
            });

        this.offerDeclineFormGroup
            .get('facilityId')
            .valueChanges.pipe(
                filter((data: string | ListItem) => typeof data === 'string' && data.trim().length > 2),
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.isFacilityLoading = true;
                }),
                untilDestroyed(this)
            )
            .subscribe((data) => {
                this.doFacilitySearch.emit(data);
            });

        this.filterUniqueCategories();
    }

    get selectedSubReasonsText(): string {
        if (!this.selectedReasonId || this.selectedReasonId.length === 0) {
            return 'None selected';
        }
        if (this.selectedReasonId.length === 1) {
            return this.selectedReasonId[0].name;
        }

        return `${this.selectedReasonId[0].name} (+${this.selectedReasonId.length - 1} others)`;
    }

    clearFormValidationOnReasonChange(controls: Array<AbstractControl<unknown, unknown>>) {
        controls.forEach((element) => {
            element.clearValidators();
            element.updateValueAndValidity();
        });
    }

    reasonSelection(event) {
        const selection: any = event.value;
        if (selection) {
            this.subReasonsList =
                Array.isArray(selection.subReasons) && selection.subReasons.length ? selection.subReasons : [];
            const subReasonIdsControl = this.offerDeclineFormGroup.get('subReasonIds');
            const vendorIdControl = this.offerDeclineFormGroup.get('vendorId');
            const facilityIdControl = this.offerDeclineFormGroup.get('facilityId');
            const payControl = this.offerDeclineFormGroup.get('pay');
            const isExistingVendorForCandidateControl = this.offerDeclineFormGroup.get('isExistingVendorForCandidate');
            const otherReasonControl = this.offerDeclineFormGroup.get('reasonText');

            if (selection.id === this.reasonEnum.SigningAya) {
                this.subReasonQuestionLabel = 'Why did the candidate choose the other offer?*';
            } else {
                this.subReasonQuestionLabel = 'Why did the candidate select this agency?*';
            }

            const listFormControls = [
                subReasonIdsControl,
                vendorIdControl,
                facilityIdControl,
                payControl,
                isExistingVendorForCandidateControl,
                otherReasonControl
            ];
            this.clearFormValidationOnReasonChange(listFormControls);
            this.offerDeclineFormGroup.get('reasonId').patchValue(selection.id);
            switch (selection.id) {
                case this.reasonEnum.SigningAya:
                    subReasonIdsControl.setValidators(Validators.required);
                    facilityIdControl.setValidators(Validators.required);
                    subReasonIdsControl.updateValueAndValidity();
                    facilityIdControl.updateValueAndValidity();
                    break;

                case this.reasonEnum.SigningDifferentFacility:
                    vendorIdControl.setValidators(Validators.required);
                    subReasonIdsControl.setValidators(Validators.required);
                    facilityIdControl.setValidators(Validators.required);
                    isExistingVendorForCandidateControl.setValidators(Validators.required);
                    vendorIdControl.updateValueAndValidity();
                    subReasonIdsControl.updateValueAndValidity();
                    facilityIdControl.updateValueAndValidity();
                    isExistingVendorForCandidateControl.updateValueAndValidity();
                    break;

                case this.reasonEnum.SigningThisFacility:
                    vendorIdControl.setValidators(Validators.required);
                    subReasonIdsControl.setValidators(Validators.required);
                    isExistingVendorForCandidateControl.setValidators(Validators.required);
                    vendorIdControl.updateValueAndValidity();
                    subReasonIdsControl.updateValueAndValidity();
                    isExistingVendorForCandidateControl.updateValueAndValidity();
                    break;

                default:
                    this.showPayFields = false;
                    break;
            }
        }
    }

    resetForm() {
        this.offerDeclineFormGroup.reset();
    }

    checkValue = (agency: VendorOptions): string => {
        const vendorNameControl = this.offerDeclineFormGroup.get('vendorName');
        if (agency && agency.id === 0) {
            vendorNameControl.setValidators(Validators.required);
        } else {
            vendorNameControl.clearValidators();
        }
        vendorNameControl.updateValueAndValidity();
        return agency ? `${agency.legalName}` : '';
    };

    updateSelectedReasons() {
        const selectedValues = this.offerDeclineFormGroup.get('subReasonIds')?.value || [];
        this.selectedReasonId = selectedValues;
    }

    checkFacilityValue = (facility: ListItem | null): string => {
        const facilityNameControl = this.offerDeclineFormGroup.get('facilityName');
        if (facility && facility.id === 0) {
            facilityNameControl.setValidators(Validators.required);
        } else {
            facilityNameControl.clearValidators();
        }
        facilityNameControl.updateValueAndValidity();
        return facility ? `${facility.name}` : '';
    };

    subReasonSelect(event, option) {
        if (event.isUserInput && event.source.value === this.reasonEnum.Unknown) {
            if (option.selected) {
                this.disableSubReasonOtherOptions = true;
            } else {
                this.disableSubReasonOtherOptions = false;
            }
        } else if (option.selected && event.isUserInput && event.source.value !== this.reasonEnum.Unknown) {
            this.disableSubReasonUnknown = true;
        }
    }

    checkSubReasonDisabled(option) {
        if (!this.disableSubReasonOtherOptions && option && option.value !== this.reasonEnum.Unknown) {
            return false;
        } else if (this.disableSubReasonOtherOptions && option && option.value !== this.reasonEnum.Unknown) {
            return true;
        } else if (this.disableSubReasonUnknown && option && option.value === this.reasonEnum.Unknown) {
            return true;
        } else if (!this.disableSubReasonUnknown && option && option.value === this.reasonEnum.Unknown) {
            return false;
        } else {
            return false;
        }
    }

    filterUniqueCategories(): void {
        const categoryMap = new Map<number, string>();

        this.declineReasons.forEach((reason) => {
            if (!categoryMap.has(reason.categoryId)) {
                categoryMap.set(reason.categoryId, reason.categoryName);
            }
        });

        this.uniqueCategories = Array.from(categoryMap).map(([categoryId, categoryName]) => ({
            categoryId,
            categoryName
        }));
    }

    filterReasonsByCategoryId(categoryId: number): void {
        this.filteredReasons = this.declineReasons.filter((reason) => reason.categoryId === categoryId);
    }

    onSubmit() {
        this.offerDeclineFormGroup.markAllAsTouched();
        if (this.offerDeclineFormGroup.invalid) {
            return;
        } else {
            const formValue = this.offerDeclineFormGroup.value;
            const cleanedUpFormValues: DeclineOfferUpdate = {
                reasonId: formValue.reasonId,
                facilityId: formValue.facilityId ? formValue.facilityId.id : null,
                facilityName: formValue.facilityName,
                reasonText: formValue.otherReasonText
                    ? formValue.reasonText.concat(' \n', formValue.otherReasonText)
                    : formValue.reasonText,
                subReasonIds: Array.isArray(formValue.subReasonIds)
                    ? (formValue.subReasonIds as Array<{ id: number }>).map((sub: { id: number }) => sub.id)
                    : [],
                payRate: formValue.payRate ? formValue.payRate : null,
                payFreq: formValue.payFreq ? formValue.payFreq : '',
                vendorId: formValue.vendorId ? formValue.vendorId.id : null,
                vendorName: formValue.vendorName,
                isExistingVendorForCandidate: formValue.isExistingVendorForCandidate
            };
            this.dialogRef.close(cleanedUpFormValues);
        }
    }

    close(): void {
        const panel = this.slideOutPanel.nativeElement;

        panel.classList.add('slide-out');
        setTimeout(() => {
            this.dialogRef.close();
        }, 300);
    }
}
